/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MainLayout from "../../../../layouts/MainLayout";
import Form from "../../../../components/forms/Form";
import "../../../../assets/styles/custom-styles.css";
import { unitAssociationFields } from "../../../../constants/forms/unitFields";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import useForm from "../../../../hooks/useForm";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
  transformFormDataForApi,
} from "../../../../utils/objectUtils";
import useBaseService from "../../../../hooks/services/useBaseService";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import SectionHeader from "../../../../components/forms/SectionHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useRemoteSelectOptions from "../../../../hooks/useRemoteSelectOptions";
import useUrlFilters from "../../../../hooks/useUrlFilters";
import { transformFiltersForUrl } from "../../../../utils/transformFiltersForUrl";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";

const AttachUnitsRelatedPage = () => {
  const navigate = useNavigate();
  const { unitId } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { filters, pagination } = useUrlFilters(
    {
      id: [],
      name: [],
      abbreviation: [],
      deleted_at: false,
    },
    "equipments_units",
  );

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const {
    get: baseServiceGet,
    post: baseServicePost,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues(unitAssociationFields),
    unitAssociationFields,
  );

  const { data: unitData } = useQueryWithLoader({
    queryKey: ["equipment_unit", unitId],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.units.unitsRelated.get(unitId),
        { deleted_at: false },
      );
      return response.result.data;
    },
    staleTime: 60 * 1000,
  });

  // --- Setar unidades relacionadas no formulário ---
  useEffect(() => {
    // Garante que só tentaremos fazer o map se unitData for REALMENTE um array
    if (unitData && Array.isArray(unitData)) {
      setFormData({
        units: unitData.map((unit) => unit.id),
      });
    } else if (
      unitData &&
      typeof unitData === "object" &&
      Array.isArray(unitData.data)
    ) {
      // Caso o unitData ainda seja o objeto de paginação
      setFormData({
        units: unitData.data.map((unit) => unit.id),
      });
    }
  }, [setFormData, unitData]);

  // --- Select de unidades ---
  const unitsSelect = useRemoteSelectOptions({
    fieldId: "units",
    queryKey: ["equipment_attached_units"],
    fetcher: async ({ page = 1, perPage = 15, search = "" }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      });
      const params = { filters, page, perPage };

      const response = await baseServiceGet(entities.units.get, params);
      return response.result;
    },
    mapToOption: (unit) => ({ value: unit.id, label: unit.name }),
    setFormData,
    formData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    isMulti: true,
  });

  // --- Selects ---
  const selects = {
    units: unitsSelect,
  };

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [selects],
  );

  // --- Mutação para sincronizar unidades relacionadas ---
  const syncUnitsMutation = useMutation({
    mutationFn: async (dataToSync) => {
      showLoader();
      setFormErrors({});
      const response = await baseServicePost(
        entities.units.unitsRelated.create(unitId),
        dataToSync,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response.status === 201) {
        navigate(`/aluguel/unidades`, { state: { type: "success" } });
        queryClient.invalidateQueries({
          queryKey: ["equipment_units", filters, pagination],
        });
        queryClient.invalidateQueries({ queryKey: ["equipment_unit", unitId] });
        queryClient.invalidateQueries({
          queryKey: ["equipment_attached_units"],
        });
      }
    },
    onError: (err) => {
      console.error("onError ", err);
      if (err && err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const { units } = transformFormDataForApi(formData);
      const cleanedPayload = cleanPayload(units);
      await syncUnitsMutation.mutateAsync({
        units: cleanedPayload,
      });
    } catch (error) {
      console.error("Erro ao sincronizar unidades relacionadas:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData.units, syncUnitsMutation]);

  const handleBack = useCallback(() => {
    navigate("/aluguel/unidades");
  }, [navigate]);

  // useEffect para mensagens de estado de localização (mantido)
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  // --- Funções para Selects (getOptions e getSelectedValue) ---
  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.options || [];
    },
    [selects],
  );

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [selects],
  );

  // Consolidação dos estados de carregamento para o loader global do formulário
  const isFormLoading = syncUnitsMutation.isPending;

  const renderForm = useCallback(() => {
    const renderedSections = new Set();

    return unitAssociationFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
            getOptions={getOptions}
            getSelectedValue={getSelectedValue}
            getSelectMeta={getSelectMeta}
          />
        </div>
      );
    });
  }, [formData, formErrors, handleChange, getOptions, getSelectedValue]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Associar Unidades Relacionadas"
        showBackButton={true}
        backUrl="/aluguel/unidades"
      />
      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          className="p-3 mt-2 rounded shadow-sm mb-2"
          textSubmit="Associar Unidade"
          textLoadingSubmit="Associando..."
          handleBack={handleBack}
          isLoading={isFormLoading}
        >
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default AttachUnitsRelatedPage;
